import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@parrot-software/design-system';
import { Calendar as CalendarIcon } from '@parrot-software/design-system/icons';
import { DateRangePresets } from '@reports/domain/types';
import type { DateRangePreset } from '@reports/domain/types';
import { createReportDateRangeFromPreset } from '@reports/domain/value-objects';
import type { ReportDateRange } from '@reports/domain/value-objects';
import { ReportCalendar } from '@reports/presentation/components/base/ReportCalendar';
import { formatDate } from '@reports/presentation/utils';

interface ReportDateRangePickerProps {
  isFiltersLoading: boolean;
  value: ReportDateRange;
  onChange: (dateRange: ReportDateRange) => void;
}

/**
 * ReportDateRangePicker - Date range picker with preset options
 *
 * Combines a dropdown menu with preset options and a calendar popup for custom date range selection.
 * Defaults to "Hoy" (Today) preset.
 *
 * @param {ReportDateRangePickerProps} props - Component properties
 * @param {boolean} props.isFiltersLoading - Whether the filters catalog is loading
 * @param {ReportDateRange} props.value - Current date range
 * @param {(dateRange: ReportDateRange) => void} props.onChange - Callback when date range changes
 * @returns {React.ReactElement} Date range picker component
 *
 * @example
 * <ReportDateRangePicker
 *   isFiltersLoading={false}
 *   value={dateRange}
 *   onChange={updateDateRange}
 * />
 */
export const ReportDateRangePicker = ({ isFiltersLoading, value, onChange }: ReportDateRangePickerProps) => {
  const { t } = useTranslation(['reports']);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Format date range for display
  const displayText = `${formatDate(value.start)}-${formatDate(value.end)}`;

  // Handle preset selection
  const handlePresetSelect = (preset: DateRangePreset, event?: React.MouseEvent) => {
    event?.stopPropagation();

    if (preset === DateRangePresets.DATE_RANGE) {
      // Small delay to ensure DropdownMenu closes before Popover opens
      setTimeout(() => {
        setIsCalendarOpen(true);
      }, 300);
      return;
    }

    const dateRange = createReportDateRangeFromPreset(preset);
    onChange(dateRange);
    setIsCalendarOpen(false);
  };

  const handleCloseCalendar = useCallback(() => {
    setIsCalendarOpen(false);
  }, []);

  return (
    <div className="flex w-full md:w-auto items-center gap-2">
      <Popover modal={false} onOpenChange={!isFiltersLoading ? setIsCalendarOpen : undefined} open={isCalendarOpen}>
        <PopoverTrigger asChild>
          <div className="w-full md:w-auto">
            <DropdownMenu onOpenChange={handleCloseCalendar}>
              <DropdownMenuTrigger asChild>
                <Button className="justify-start w-full md:min-w-[248px]" disabled={isFiltersLoading} variant="outline">
                  <div className="flex items-center gap-2">
                    <CalendarIcon size={16} />
                    <span className="text-sm font-normal">{displayText}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[248px]">
                <DropdownMenuItem
                  className={value.preset === DateRangePresets.TODAY ? 'bg-accent' : undefined}
                  onClick={(e) => handlePresetSelect(DateRangePresets.TODAY, e)}
                >
                  {t('reportCommon:dateRange.presets.today')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={value.preset === DateRangePresets.YESTERDAY ? 'bg-accent' : undefined}
                  onClick={(e) => handlePresetSelect(DateRangePresets.YESTERDAY, e)}
                >
                  {t('reportCommon:dateRange.presets.yesterday')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={value.preset === DateRangePresets.THIS_WEEK ? 'bg-accent' : undefined}
                  onClick={(e) => handlePresetSelect(DateRangePresets.THIS_WEEK, e)}
                >
                  {t('reportCommon:dateRange.presets.thisWeek')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={value.preset === DateRangePresets.THIS_MONTH ? 'bg-accent' : undefined}
                  onClick={(e) => handlePresetSelect(DateRangePresets.THIS_MONTH, e)}
                >
                  {t('reportCommon:dateRange.presets.thisMonth')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={value.preset === DateRangePresets.THIS_YEAR ? 'bg-accent' : undefined}
                  onClick={(e) => handlePresetSelect(DateRangePresets.THIS_YEAR, e)}
                >
                  {t('reportCommon:dateRange.presets.thisYear')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={value.preset === DateRangePresets.DATE_RANGE ? 'bg-accent' : undefined}
                  onClick={(e) => handlePresetSelect(DateRangePresets.DATE_RANGE, e)}
                >
                  {t('reportCommon:dateRange.presets.dateRange')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-auto p-0">
          <ReportCalendar isOpen={isCalendarOpen} onChange={onChange} onOpenChange={setIsCalendarOpen} value={value} />
        </PopoverContent>
      </Popover>
    </div>
  );
};
